import React, { useState } from "react";
import axios from "axios";
import { EmailGuard } from "../App";
import { AuthGuard } from "../App";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaMoneyBillWave, FaSearch, FaSpinner, FaExclamationCircle } from 'react-icons/fa';
import { Oval } from "react-loader-spinner";
import { StandardURL } from "../App";

// Helper function to format currency
const formatCurrency = (amount) => {
    // Assuming the balance might be a string or number, ensure it's handled as a number
    const numericAmount = parseFloat(amount) || 0;
    // Format to Kenyan Shillings (Ksh) or a standard currency format
    return new Intl.NumberFormat('en-KE', { 
        style: 'currency', 
        currency: 'Ksh', 
        minimumFractionDigits: 2, // Ensure two decimal places for currency
    }).format(numericAmount).replace('Ksh', 'Ksh ');
};

const OwnerBalancePage = () => {
    const [propertyId, setPropertyId] = useState("");
    const [ownerBalance, setOwnerBalance] = useState(null);
    const [loading, setLoading] = useState(false); // Added loading state for UI
    const [error, setError] = useState(null);     // Added error state for UI
    const [dataFetched, setDataFetched] = useState(false); // Track if a fetch attempt has been made

    const fetchOwnerBalance = async () => {
        const token = localStorage.getItem("token");

        // Basic validation
        if (!propertyId.trim()) {
            toast.error("Please enter a Property ID.");
            return;
        }
        
        if (!token) {
            toast.error("Authentication token missing.");
            return;
        }

        setLoading(true);
        setError(null);
        setOwnerBalance(null);
        setDataFetched(false);

        try {
            const response = await axios.get(
                // Functionality preserved: Fetch balance using propertyId
                `${StandardURL}/iNyumba/balance/owner/${propertyId.trim()}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const balanceData = response.data.response;
            
            if (Array.isArray(balanceData) && balanceData.length > 0) {
                // Functionality preserved: set balance from the first item
                setOwnerBalance(balanceData[0]);
                toast.success(`Balance fetched successfully for Property ID: ${propertyId}.`);
            } else {
                setOwnerBalance(null);
                toast.info(`No balance record found for Property ID: ${propertyId}.`);
            }
        } catch (err) {
            // Error handling improved with toast and UI state
            const errorMessage = err.response?.data?.response || "Failed to fetch owner balance. Please check the Property ID.";
            setError(errorMessage);
            toast.error(errorMessage);
            console.error("Error fetching owner balance:", err);
        } finally {
            setLoading(false);
            setDataFetched(true);
        }
    };

    const handleInputChange = (e) => {
        // Functionality preserved
        setPropertyId(e.target.value);
    };

    const handleEnterKeyPress = (e) => {
        // Functionality preserved
        if (e.key === "Enter") {
            fetchOwnerBalance();
        }
    };

    const renderContent = () => {
        if (loading) {
            return (
                <div className="flex flex-col items-center justify-center p-12 bg-white rounded-xl shadow-md">
                    <Oval color="#3b82f6" secondaryColor="#93c5fd" height={50} width={50} ariaLabel="loading-spinner" />
                    <p className="mt-4 text-lg text-blue-600">Fetching owner balance...</p>
                </div>
            );
        }

        if (error) {
            return (
                <div className="p-8 text-center bg-red-50 border-l-4 border-red-500 text-red-700 rounded-lg shadow-md">
                    <FaExclamationCircle className="w-8 h-8 mx-auto mb-3" />
                    <p className="font-semibold text-lg">Error</p>
                    <p className="text-sm">{error}</p>
                </div>
            );
        }
        
        if (ownerBalance) {
            return (
                <div className="bg-white shadow-2xl rounded-xl p-8 transform hover:scale-[1.02] transition duration-300 border-t-4 border-green-500 max-w-lg mx-auto">
                    <div className="flex items-center justify-center mb-6">
                        <FaMoneyBillWave className="w-10 h-10 text-green-600 mr-3" />
                        <h2 className="text-2xl font-bold text-gray-800">Owner Payout Balance</h2>
                    </div>
                    
                    <div className="space-y-4">
                        {/* Property ID Detail */}
                        <div className="flex justify-between items-center border-b pb-2">
                            <span className="text-lg font-medium text-gray-500">Property ID:</span>
                            <span className="text-xl font-extrabold text-blue-600 bg-blue-50 px-3 py-1 rounded-md">
                                {ownerBalance.property_id || 'N/A'}
                            </span>
                        </div>
                        
                        {/* Balance Detail: Use formatCurrency helper */}
                        <div className="pt-2">
                            <p className="text-xl font-medium text-gray-600 mb-2">Current Balance:</p>
                            <div className="text-5xl font-extrabold text-gray-900 break-words">
                                {formatCurrency(ownerBalance.balance)}
                            </div>
                            <p className="text-sm text-gray-400 mt-2">
                                This is the total outstanding balance due to the owner.
                            </p>
                        </div>
                    </div>
                </div>
            );
        }

        if (dataFetched) {
             return (
                <div className="p-8 text-center bg-yellow-50 border-l-4 border-yellow-500 text-yellow-700 rounded-lg shadow-md max-w-lg mx-auto">
                    <FaExclamationCircle className="w-8 h-8 mx-auto mb-3" />
                    <p className="font-semibold text-lg">No Balance Found</p>
                    <p className="text-sm">The Property ID was searched, but no owner balance record was returned. Check the ID and try again.</p>
                </div>
             )
        }
        
        // Initial state prompt
        return (
            <div className="p-12 text-center text-gray-500 bg-white rounded-xl shadow-md max-w-lg mx-auto">
                <FaSearch className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                <p className="text-lg font-medium">Search for an Owner's Balance</p>
                <p className="text-sm">Enter a property ID above to view the outstanding balance owed to the owner.</p>
            </div>
        );
    };

    return (
        <EmailGuard>
            <AuthGuard>
                <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
                    
                    <div className="max-w-4xl mx-auto">
                        <h1 className="text-3xl font-extrabold text-gray-900 flex items-center justify-center mb-10 border-b pb-4">
                            <FaMoneyBillWave className="mr-3 text-blue-600" /> Owner Balance Lookup
                        </h1>

                        {/* Input/Search Section (Improved UI) */}
                        <div className="bg-white shadow-xl rounded-xl p-6 mb-10 flex flex-col sm:flex-row gap-4 items-center justify-center">
                            <input
                                type="text"
                                value={propertyId}
                                onChange={handleInputChange}
                                onKeyPress={handleEnterKeyPress}
                                placeholder="Enter Property ID (e.g., 101 or A101)"
                                className="w-full sm:w-80 border border-gray-300 px-4 py-3 rounded-lg focus:ring-blue-500 focus:border-blue-500 shadow-sm transition duration-150"
                                aria-label="Property ID Input"
                            />
                            <button
                                onClick={fetchOwnerBalance}
                                className="w-full sm:w-auto bg-blue-600 text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-blue-700 transition duration-300 ease-in-out disabled:opacity-50 flex items-center justify-center"
                                disabled={loading || !propertyId.trim()}
                            >
                                {loading ? (
                                    <>
                                        <FaSpinner className="animate-spin mr-2" />
                                        Fetching...
                                    </>
                                ) : (
                                    <>
                                        <FaSearch className="mr-2" />
                                        Fetch Balance
                                    </>
                                )}
                            </button>
                        </div>
                        
                        {/* Content/Result Section */}
                        {renderContent()}

                    </div>
                </div>
            </AuthGuard>
        </EmailGuard>
    );
};

export default OwnerBalancePage;